/* eslint-env browser, es6 */
import { BOARD_SIZE, HARD_CODED_BOARD, MIN_WORD_LENGTH } from "./constants";
import { LETTER_FREQUENCY, WORDS_LIST } from "./words";

const TOTAL_LETTERS = BOARD_SIZE * BOARD_SIZE;

type Coordinate = [number, number];
type Path = Coordinate[];

export interface Cell {
  letter: string;
  highlightIndex: number | null;
}

export class Board {
  public letters: string[];
  public paths: Path[];

  constructor(letters: string[]) {
    this.letters = letters;
    this.paths = [];
  }

  at(row: number, col: number): string {
    return this.letters[row * BOARD_SIZE + col];
  }

  atIndex(index: number): string {
    return this.letters[index];
  }

  addToHighlight(letter: string): boolean {
    const newPaths: Path[] = [];
    if (this.paths.length > 0) {
      for (const path of this.paths) {
        const [lastX, lastY] = path[path.length - 1];
        for (const neighborXy of getNeighbors(lastX, lastY)) {
          const [x, y] = neighborXy;
          if (this.at(x, y) === letter && !alreadyUsed(path, x, y)) {
            const pathCopy = path.slice();
            pathCopy.push([x, y]);
            newPaths.push(pathCopy);
          }
        }
      }
    } else {
      for (let row = 0; row < BOARD_SIZE; row++) {
        for (let column = 0; column < BOARD_SIZE; column++) {
          const letterFromBoard = this.at(row, column);
          if (letterFromBoard === letter) {
            newPaths.push([[row, column]]);
          }
        }
      }
    }

    this.paths = newPaths;
    if (this.paths.length > 0) {
      return true;
    } else {
      return false;
    }
  }

  clearHighlighted(): void {
    this.paths = [];
  }

  getCurrentWord(): string | null {
    if (this.paths.length === 0) {
      return null;
    }

    const wordBuilder = [];
    for (const [x, y] of this.paths[0]) {
      wordBuilder.push(this.at(x, y));
    }
    return wordBuilder.length > 0 ? wordBuilder.join("") : null;
  }

  rows(): Cell[][] {
    let highlightPath: Path = [];
    for (const path of this.paths) {
      if (path.length > 0) {
        highlightPath = path;
        break;
      }
    }

    const rows: Cell[][] = [];
    for (let row = 0; row < BOARD_SIZE; row++) {
      const rowArray = [];
      for (let col = 0; col < BOARD_SIZE; col++) {
        const letter = this.at(row, col);
        const index = highlightPath.findIndex(
          (rowCol) => rowCol[0] === row && rowCol[1] === col
        );
        const highlightIndex =
          index === -1 ? null : highlightPath.length - (index + 1);
        rowArray.push({ letter, highlightIndex });
      }
      rows.push(rowArray);
    }
    return rows;
  }
}

export function getAllWords(board: Board): Set<string> {
  const wordsSet = new Set<string>();
  for (let i = 0; i < TOTAL_LETTERS; i++) {
    const letter = board.atIndex(i);
    getAllWordsHelper(board, WORDS_LIST, letter, i, new Set([i]), wordsSet);
  }
  return wordsSet;
}

function getAllWordsHelper(
  board: Board,
  wordList: string[],
  wordSoFar: string,
  lastIndex: number,
  alreadyUsedSet: Set<number>,
  wordsSet: Set<string>
): void {
  const wordListIndex = binarySearch(wordList, wordSoFar);
  if (wordListIndex === wordList.length) {
    return;
  }

  const dictionaryWord = wordList[wordListIndex];
  if (dictionaryWord === wordSoFar && wordSoFar.length >= MIN_WORD_LENGTH) {
    // We have found a valid word.
    wordsSet.add(wordSoFar);
  }

  if (!dictionaryWord.startsWith(wordSoFar)) {
    // No word in the dictionary starts with this sequence, so we can terminate
    // early.
    return;
  }

  const [thisRow, thisCol] = indexToRowCol(lastIndex);
  for (const [row, col] of getNeighbors(thisRow, thisCol)) {
    const nextIndex = rowColToIndex(row, col);
    if (alreadyUsedSet.has(nextIndex)) {
      continue;
    }

    const nextWord = wordSoFar + board.atIndex(nextIndex);
    const nextSet = new Set(alreadyUsedSet.values());
    nextSet.add(nextIndex);
    getAllWordsHelper(board, wordList, nextWord, nextIndex, nextSet, wordsSet);
  }
}

function binarySearch<T>(haystack: T[], needle: T): number {
  let low = 0;
  let high = haystack.length;
  while (low < high) {
    const mid = Math.floor((high + low) / 2);
    if (haystack[mid] === needle) {
      return mid;
    } else if (haystack[mid] < needle) {
      low = mid + 1;
    } else {
      high = mid;
    }
  }
  return low;
}

export function generateRandomBoard(): Board {
  if (HARD_CODED_BOARD.length > 0) {
    return new Board(HARD_CODED_BOARD);
  }

  const letters = [];
  for (let i = 0; i < TOTAL_LETTERS; i++) {
    const index = Math.floor(randomNumber(0, LETTER_FREQUENCY.length));
    letters.push(LETTER_FREQUENCY[index]);
  }
  return new Board(letters);
}

function getNeighbors(row: number, col: number): Coordinate[] {
  const neighbors: Coordinate[] = [];
  const deltas = [
    [+0, +1], // right
    [+1, +1], // right and down
    [+1, +0], // down
    [+1, -1], // left and down
    [+0, -1], // left
    [-1, -1], // left and up
    [-1, +0], // up
    [-1, +1], // right and up
  ];
  for (const delta of deltas) {
    const [rowDelta, colDelta] = delta;
    const newRow = row + rowDelta;
    const newCol = col + colDelta;
    if (inRange(newRow, newCol)) {
      neighbors.push([newRow, newCol]);
    }
  }
  return neighbors;
}

function indexToRowCol(index: number): Coordinate {
  const row = Math.floor(index / BOARD_SIZE);
  const col = index % BOARD_SIZE;
  return [row, col];
}

function rowColToIndex(row: number, col: number): number {
  return row * BOARD_SIZE + col;
}

function alreadyUsed(path: Path, x: number, y: number): boolean {
  for (const xy of path) {
    const [thisX, thisY] = xy;
    if (x === thisX && y === thisY) {
      return true;
    }
  }

  return false;
}

function inRange(row: number, col: number): boolean {
  return 0 <= row && row < BOARD_SIZE && 0 <= col && col < BOARD_SIZE;
}

function randomNumber(min: number, max: number): number {
  return Math.random() * (max - min) + min;
}
